import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldOff, X as XIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/api/client";
import {
  getMyMailboxAccess,
  grantMailboxAccess,
  listMailboxAccess,
  revokeMailboxAccess,
  type MailboxAccessOut,
} from "@/api/mailbox-access";
import type { MailboxResponse, UserSummary } from "@/api/schemas";
import { listOrgUsers } from "@/api/users";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/stores/auth";

const MY_ACCESS_QUERY = ["mailbox-access", "me"] as const;
const MAILBOXES_QUERY = ["mailboxes"] as const;
const ORG_USERS_QUERY = ["users", "org"] as const;

function MailboxAccessPage() {
  const role = useAuth((s) => s.user?.role);
  const isSuperadmin = role === "superadmin";

  const myAccessQuery = useQuery({
    queryKey: MY_ACCESS_QUERY,
    queryFn: getMyMailboxAccess,
  });
  const mailboxesQuery = useQuery({
    queryKey: MAILBOXES_QUERY,
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
  });

  const delegatedSet = useMemo(
    () => new Set(myAccessQuery.data?.delegated_mailbox_ids ?? []),
    [myAccessQuery.data],
  );

  const allowedMailboxes = useMemo<MailboxResponse[]>(() => {
    const all = mailboxesQuery.data ?? [];
    if (isSuperadmin) return all;
    return all.filter((m) => delegatedSet.has(m.id));
  }, [mailboxesQuery.data, delegatedSet, isSuperadmin]);

  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);

  const effectiveMailboxId =
    selectedMailbox && allowedMailboxes.some((m) => m.id === selectedMailbox)
      ? selectedMailbox
      : (allowedMailboxes[0]?.id ?? null);

  if (myAccessQuery.isLoading || mailboxesQuery.isLoading) {
    return (
      <div className="mx-auto flex max-w-4xl items-center gap-2 px-6 py-8 text-sm text-text-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-medium tracking-tight">Mailbox access</h1>
        <p className="mt-1 text-sm text-text-muted">
          Choose which agents can see tickets from each mailbox you administer.
          Superadmins delegate which mailboxes you control.
        </p>
      </div>

      {allowedMailboxes.length === 0 ? (
        <NoDelegationsEmpty isSuperadmin={isSuperadmin} />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <Label htmlFor="mailbox-picker" className="text-sm">
              Mailbox
            </Label>
            <Select
              value={effectiveMailboxId ?? undefined}
              onValueChange={(v) => setSelectedMailbox(v)}
            >
              <SelectTrigger id="mailbox-picker" className="w-80">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedMailboxes.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.email_address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {effectiveMailboxId && (
            <MailboxAccessPanel mailboxId={effectiveMailboxId} />
          )}
        </div>
      )}
    </div>
  );
}

function NoDelegationsEmpty({ isSuperadmin }: { isSuperadmin: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-surface px-6 py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
        <ShieldOff className="h-4 w-4 text-text-muted" />
      </div>
      <div className="text-sm font-medium">No delegated mailboxes yet</div>
      <div className="mt-1 max-w-md text-xs text-text-muted">
        {isSuperadmin
          ? "Connect a mailbox first to manage access."
          : "A superadmin needs to delegate authority over at least one mailbox before you can configure who sees what."}
      </div>
    </div>
  );
}

function MailboxAccessPanel({ mailboxId }: { mailboxId: string }) {
  const qc = useQueryClient();
  const grantsQuery = useQuery({
    queryKey: ["mailbox-access", mailboxId],
    queryFn: () => listMailboxAccess(mailboxId),
  });
  const usersQuery = useQuery({
    queryKey: ORG_USERS_QUERY,
    queryFn: listOrgUsers,
  });

  const grants = useMemo(() => grantsQuery.data ?? [], [grantsQuery.data]);
  const grantedIds = useMemo(() => new Set(grants.map((g) => g.user_id)), [grants]);
  const candidates = useMemo<UserSummary[]>(() => {
    if (!usersQuery.data) return [];
    return usersQuery.data.filter((u) => u.is_active && !grantedIds.has(u.id));
  }, [usersQuery.data, grantedIds]);

  const grantMutation = useMutation({
    mutationFn: (userId: string) =>
      grantMailboxAccess({ mailbox_id: mailboxId, user_id: userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mailbox-access", mailboxId] });
      toast.success("Access granted");
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not grant access");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (accessId: string) => revokeMailboxAccess(accessId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mailbox-access", mailboxId] });
      toast.success("Access revoked");
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not revoke access");
    },
  });

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <Label className="mb-2 block">Current access</Label>
        {grantsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : grants.length === 0 ? (
          <div className="rounded border border-border bg-surface px-3 py-2 text-xs text-text-muted">
            Nobody has access to this mailbox yet.
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {grants.map((g) => (
              <GrantRow
                key={g.id}
                grant={g}
                onRevoke={() => revokeMutation.mutate(g.id)}
                disabled={revokeMutation.isPending}
              />
            ))}
          </ul>
        )}
      </div>

      <div>
        <Label className="mb-2 block">Add access</Label>
        <Command>
          <CommandInput placeholder="Search by name or email…" />
          <CommandList>
            <CommandEmpty>
              {usersQuery.isLoading ? "Loading…" : "No matching users."}
            </CommandEmpty>
            <CommandGroup>
              {candidates.map((u) => (
                <CommandItem
                  key={u.id}
                  value={`${u.name} ${u.email}`}
                  onSelect={() => grantMutation.mutate(u.id)}
                  disabled={grantMutation.isPending}
                >
                  <span className="font-medium">{u.name}</span>
                  <span className="ml-2 text-text-muted">{u.email}</span>
                  <span className="ml-auto text-xs text-text-faint">{u.role}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </div>
    </div>
  );
}

function GrantRow({
  grant,
  onRevoke,
  disabled,
}: {
  grant: MailboxAccessOut;
  onRevoke: () => void;
  disabled: boolean;
}) {
  return (
    <li className="flex items-center justify-between rounded border border-border px-2 py-1.5 text-xs">
      <span>
        <span className="font-medium">{grant.user_name}</span>{" "}
        <span className="text-text-muted">{grant.user_email}</span>
        <span className="ml-2 rounded bg-surface-subtle px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
          {grant.user_role}
        </span>
      </span>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Revoke ${grant.user_name}`}
        disabled={disabled}
        onClick={onRevoke}
      >
        <XIcon className="h-3 w-3" />
      </Button>
    </li>
  );
}

export const Route = createFileRoute("/_auth/settings/mailbox-access")({
  component: MailboxAccessPage,
});
